import * as ngCore from '@angular/core'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import * as me from '../../../me'
import * as routeBound from '../../../route-bound'
import * as userAccess from '../../user-access'
import * as records from '../records'

import { Api } from './api.service'
import * as effects from './effects'
import type { TypeParams } from './types'

const DEFAULT_QUANTITY = 100

/**
 * Service Centers entity collection.
 */
@ngCore.Injectable()
export class Collection extends ec.Collection<TypeParams> {
  constructor() {
    const meFeature = ngCore.inject(me.Feature)
    const surveyAdminsEc = ngCore.inject(userAccess.surveyAdmins.Collection)

    // Only platform admins, tenant admins, and survey admins can create,
    // update, or delete service centers. Computed once here (rather than
    // inside rowConfig, which runs once per row) so it isn't rebuilt on
    // every row of every render.
    const canManageSg = ngCore.computed(
      (
        canManageUsers = meFeature.contexts.selectors.currentUserCanManageUsers.asSignal(),
        currentUserId = meFeature.selectors.currentUserId.asSignal(),
        surveyAdminUserIds = new Set(
          surveyAdminsEc.plugins.currentListPlugin.feature.selectors.entities
            .asSignal()
            .map(({ userId }) => userId),
        ),
      ) =>
        canManageUsers ||
        (!!currentUserId && surveyAdminUserIds.has(currentUserId)),
    )

    super({
      api: ngCore.inject(Api),
      context$: ngCore.inject(routeBound.scenario.Context.TOKEN),
      identify: serviceCenter => serviceCenter.id,
      name: ec.Name.Predefined.SpaceServiceCenter,
      plugins: {
        additionalFactories: [
          new ds.table.ec.Factory<TypeParams>(collection => ({
            rowConfig: ({ entity: { id } }) => ({
              iconsConfigs: canManageSg()
                ? [
                    {
                      class: 'atn-icon atn-edit atn-blue',
                      onClick: () =>
                        collection.plugins.editingPlugin.feature.actions.edit
                          .from(`Table.Row-${id}.Icons.Edit.Clicked`)
                          .dispatch({ id }),
                    },
                    {
                      class: 'atn-icon atn-trash atn-red',
                      onClick: () =>
                        collection.plugins.editingPlugin.feature.actions.markForDeletion
                          .from(`Table.Row-${id}.Icons.Trash.Clicked`)
                          .dispatch({ id }),
                    },
                  ]
                : [],
            }),
          })),
        ],
        currentList: {
          quantity: {
            get: quantity => quantity ?? DEFAULT_QUANTITY,
          },
        },
      },
    })

    const recordsEc = ngCore.inject(records.Collection)
    this.feature.registerEffects(
      effects.reloadOnFunctionalUsageChange(this, recordsEc),
    )
  }
}
